import { Controller } from "@hotwired/stimulus";

// Plays the Assistant voice samples on User settings (Pages/User/Settings/Index.cshtml), one at a time.
//
// Each play button names its <audio preload="none"> element in aria-controls, so a sample is fetched only when
// someone presses its button. The two button labels come from the view as values; this script holds no text of
// its own. Pressing a button starts its sample and stops the one already playing. Pressing it while its sample
// plays stops it. A sample that ends, or fails to load or play, puts its button back to the play label and shows
// nothing else.
type Playing = { button: HTMLButtonElement; audio: HTMLAudioElement; reset: () => void };

export class AssistantVoiceSampleController extends Controller<HTMLElement> {
    static override values = {
        playLabel: String,
        stopLabel: String,
    };

    declare playLabelValue: string;
    declare stopLabelValue: string;

    private playing: Playing | null = null;

    toggle(event: Event): void {
        const button = event.currentTarget as HTMLButtonElement;
        const pressedWhilePlaying = this.playing?.button === button;

        this.stop();
        if (!pressedWhilePlaying) this.start(button);
    }

    override disconnect(): void {
        this.stop();
    }

    private start(button: HTMLButtonElement): void {
        const audio = this.sampleOf(button);
        if (!audio) return;

        const playing: Playing = { button, audio, reset: () => this.stop() };
        audio.addEventListener("ended", playing.reset);
        audio.addEventListener("error", playing.reset);

        this.playing = playing;
        this.show(button, this.stopLabelValue, true);
        audio.play().catch(() => {
            if (this.playing === playing) this.stop();
        });
    }

    private stop(): void {
        if (!this.playing) return;

        const { button, audio, reset } = this.playing;
        this.playing = null;
        audio.removeEventListener("ended", reset);
        audio.removeEventListener("error", reset);
        audio.pause();
        audio.currentTime = 0;
        this.show(button, this.playLabelValue, false);
    }

    private sampleOf(button: HTMLButtonElement): HTMLAudioElement | null {
        const id = button.getAttribute("aria-controls");
        const audio = id ? document.getElementById(id) : null;
        return audio instanceof HTMLAudioElement ? audio : null;
    }

    private show(button: HTMLButtonElement, label: string, pressed: boolean): void {
        button.textContent = label;
        button.setAttribute("aria-pressed", String(pressed));
    }
}
